import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useQueryPayloadWithDeps } from '@shared/hooks';
import { postPatternQuery, postPatternsQuery } from '../apis';

export const usePatternQuery = <
  T extends PatternQueryResponseItemData = PatternQueryResponseItemData,
  TQueryFnData extends Awaited<ReturnType<typeof postPatternQuery<T>>> = Awaited<
    ReturnType<typeof postPatternQuery<T>>
  >,
  TError = unknown,
  TData = TQueryFnData,
>(
  payload: PatternQueryPayload,
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  > = {},
): UseQueryResult<TData, TError> => {
  const { depKeys, getEnabledStatus, getLoadingStatus, getFetchingStatus } =
    useQueryPayloadWithDeps(payload);
  const enabled = (options.enabled ?? true) && payload.pattern.length > 0;
  const query = useQuery<TQueryFnData, TError, TData, any>({
    keepPreviousData: enabled,
    ...options,
    queryKey: ['/gm/v1/pattern/query', payload, depKeys] as const,
    queryFn: () => postPatternQuery(payload),
    enabled: getEnabledStatus(enabled),
  });

  return {
    ...query,
    isLoading: getLoadingStatus(query.isLoading),
    isFetching: getFetchingStatus(query.isFetching),
  } as typeof query;
};

export const usePatternsQuery = <
  T extends PatternQueryResponseItemData = PatternQueryResponseItemData,
  TQueryFnData extends Awaited<ReturnType<typeof postPatternsQuery<T>>> = Awaited<
    ReturnType<typeof postPatternsQuery<T>>
  >,
  TError = unknown,
  TData = TQueryFnData,
>(
  payload: PatternsQueryPayload,
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  > = {},
): UseQueryResult<TData, TError> => {
  const { depKeys, getEnabledStatus, getLoadingStatus, getFetchingStatus } =
    useQueryPayloadWithDeps(payload);

  const query = useQuery<TQueryFnData, TError, TData, any>({
    keepPreviousData: true,
    ...options,
    queryKey: ['/gm/v1/patterns/query', payload, depKeys] as const,
    queryFn: () => postPatternsQuery(payload),
    enabled: getEnabledStatus((options.enabled ?? true) && payload.patterns.length > 0),
  });

  return {
    ...query,
    isLoading: getLoadingStatus(query.isLoading),
    isFetching: getFetchingStatus(query.isFetching),
  } as typeof query;
};
